import React, { useCallback, useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { rupees, formatIndian, purityColor, formatApiError, formatWeight } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Search, Grid2x2, List, Filter, ChevronUp, ChevronDown, ChevronsUpDown, Sparkles, CheckSquare, Loader2, Building2, Trash2 } from "lucide-react";
import ProductDetailDrawer from "@/components/ProductDetailDrawer";
import PhotoPlaceholder from "@/components/PhotoPlaceholder";
import MemoBadge from "@/components/MemoBadge";
import { AshthLiteBadge } from "@/components/AshthLiteBadge";
import { isAshthLiteProduct } from "@/lib/ashthLite";
import { useAuth } from "@/contexts/AuthContext";

const CATEGORIES = ["", "Ring", "Earring", "Necklace", "Pendant", "Bracelet", "Bangle"];
const PURITIES = ["", "18KT", "14KT", "9KT", "PT950", "925"];
const CATEGORY_OPTIONS = CATEGORIES.filter(Boolean);
const PURITY_OPTIONS = PURITIES.filter(Boolean);

// CS 21.42 -- Products page: bulk "Add to wholesaler" flow. Turns HO
// catalogue designs into made-to-order entries in a chosen wholesaler's
// list. Pricing flows through her rate card automatically.
function AddToWholesalerDialog({ open, onOpenChange, jewelCodes, onDone }) {
  const [wholesalers, setWholesalers] = React.useState([]);
  const [pickedWid, setPickedWid] = React.useState("");
  const [precheck, setPrecheck] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [checking, setChecking] = React.useState(false);
  React.useEffect(() => {
    if (!open) return;
    setPrecheck(null); setPickedWid(""); setBusy(false);
    api.get("/wholesalers").then(({ data }) => {
      setWholesalers(Array.isArray(data) ? data : (data?.items || []));
    }).catch(() => toast.error("Could not load wholesalers"));
  }, [open]);
  const runPrecheck = async (wid) => {
    if (!wid || jewelCodes.length === 0) return;
    setChecking(true); setPrecheck(null);
    try {
      const { data } = await api.post(
        `/wholesalers/${wid}/designs/precheck`,
        { jewel_codes: jewelCodes });
      setPrecheck(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Precheck failed");
    } finally { setChecking(false); }
  };
  React.useEffect(() => { if (pickedWid) runPrecheck(pickedWid); }, [pickedWid, jewelCodes.join("|")]);
  const anyBlockers = precheck?.results?.some((r) => !r.ok);
  const submit = async (force) => {
    setBusy(true);
    try {
      const { data } = await api.post(
        `/wholesalers/${pickedWid}/designs`,
        { jewel_codes: jewelCodes, force: !!force });
      toast.success(`${data.total} design${data.total === 1 ? "" : "s"} added to ${data.wholesaler_name} (${data.inserted} new · ${data.updated} updated)`);
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.blockers) {
        setPrecheck({ results: detail.blockers });
      } else {
        toast.error(formatApiError(detail) || "Add failed");
      }
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-lg" data-testid="add-to-wholesaler-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-[#0D1B2A]">Add {jewelCodes.length} design{jewelCodes.length === 1 ? "" : "s"} to a wholesaler</DialogTitle>
          <p className="text-xs text-slate-500 mt-1">Designs appear on that wholesaler&apos;s portal + retailer shopfront as made-to-order pieces, priced by her rate card.</p>
        </DialogHeader>
        <div className="space-y-4 py-3">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-500 mb-1 block">Wholesaler</label>
            <Select value={pickedWid} onValueChange={setPickedWid}>
              <SelectTrigger className="h-10" data-testid="atw-wholesaler-select"><SelectValue placeholder="Choose a wholesaler…" /></SelectTrigger>
              <SelectContent>
                {wholesalers.map((w) => (
                  <SelectItem key={w.id} value={w.id} data-testid={`atw-wholesaler-${w.id}`}>{w.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {checking && <div className="text-xs text-slate-500">Checking rate-card coverage…</div>}
          {precheck && (
            <div className="border-t border-slate-100 pt-3 max-h-64 overflow-y-auto" data-testid="atw-precheck-results">
              {precheck.results.map((r) => (
                <div key={r.jewel_code} className={`text-xs py-1.5 flex items-start gap-2 ${r.ok ? "text-emerald-700" : "text-amber-700"}`} data-testid={`atw-result-${r.jewel_code}`}>
                  <span className="mt-0.5">{r.ok ? "✓" : "⚠"}</span>
                  <div className="flex-1">
                    <div className="font-mono">{r.product_code || r.jewel_code}</div>
                    {r.not_found && <div className="text-red-700">Design not found in catalogue.</div>}
                    {r.missing_shapes?.length > 0 && (
                      <div>Shapes not in rate-card: <span className="font-medium">{r.missing_shapes.join(", ")}</span> — will render as pending.</div>
                    )}
                    {r.ok && r.resolved_shapes?.length > 0 && (
                      <div className="text-slate-500">Shapes ok: {r.resolved_shapes.join(", ")}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="atw-cancel">Cancel</Button>
          {precheck && anyBlockers ? (
            <Button onClick={() => submit(true)} disabled={busy || !pickedWid}
              className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="atw-add-anyway">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Add anyway (mark pending)"}
            </Button>
          ) : (
            <Button onClick={() => submit(false)} disabled={busy || !pickedWid || !precheck}
              className="bg-[#0D1B2A] hover:bg-[#0D1B2A]/90 text-white" data-testid="atw-add">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Add"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Products() {
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState(null);
  const [franchises, setFranchises] = useState([]);
  const [distributors, setDistributors] = useState([]);
  const [search, setSearch] = useState(params.get("search") || "");
  const [source, setSource] = useState(params.get("source_tag") || "");
  const [category, setCategory] = useState("");
  const [purity, setPurity] = useState("");
  const [memoFilter, setMemoFilter] = useState(params.get("memo") || "all"); // 'all' | 'memo' | 'sold'
  const [distFilter, setDistFilter] = useState(params.get("dist") || "all"); // 'all' | 'in' | 'out'
  const [view, setView] = useState("grid");
  const [selected, setSelected] = useState(null);
  // Bulk selection mode for Distribution Catalog actions
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkSelected, setBulkSelected] = useState(() => new Set());
  const [atwOpen, setAtwOpen] = useState(false);
  // CS 21.86 (v8) — HO Products bulk-delete confirmation state. No PIN;
  // existing authenticated HO access + explicit confirmation is enough.
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  // Sort: {key: 'net_weight'|'company'|'franchise'|'customer'|'gp', dir: 'asc'|'desc'}. null = default order.
  const [sort, setSort] = useState(null);
  const { user } = useAuth();
  const showGP = user?.role === "master_admin";
  const canEditDist = user?.role === "master_admin";

  const load = useCallback(async () => {
    setItems(null);
    const query = {};
    if (search) query.search = search;
    if (source) query.source_tag = source;
    if (category) query.category = category;
    if (purity) query.purity = purity;
    if (memoFilter === "memo") query.is_memo = true;
    if (memoFilter === "sold") query.is_memo = false;
    if (distFilter === "in") query.distribution_catalog = true;
    if (distFilter === "out") query.distribution_catalog = false;
    const { data } = await api.get("/products", { params: query });
    setItems(data);
  }, [search, source, category, purity, memoFilter, distFilter]);

  useEffect(() => {
    api.get("/franchises").then((r) => setFranchises(r.data)).catch(() => {});
    api.get("/distributors").then((r) => setDistributors(r.data)).catch(() => {});
  }, []);

  // Immediate load on filter changes (excluding free-text search)
  useEffect(() => {
    load();
    // search is intentionally excluded — it is handled by the debounced effect below
  }, [source, category, purity, memoFilter, distFilter]);

  // Debounced load on free-text search input (300ms)
  useEffect(() => {
    const t = setTimeout(() => load(), 300);
    return () => clearTimeout(t);
    // load is intentionally excluded — only re-run when search text changes
  }, [search]);

  // 3-state sort cycle: null → asc → desc → null
  const cycleSort = (key) => {
    setSort((s) => {
      if (!s || s.key !== key) return { key, dir: "asc" };
      if (s.dir === "asc") return { key, dir: "desc" };
      return null;
    });
  };

  const toggleBulkPick = (jc) => {
    setBulkSelected((s) => {
      const n = new Set(s);
      if (n.has(jc)) n.delete(jc); else n.add(jc);
      return n;
    });
  };

  // CS 21.54 -- Filter-aware Select All. Selects only the products currently
  // matching the active filters/search (i.e. what's actually rendered). If
  // every rendered item is already selected, the same click clears the
  // selection back to zero. `items` is already the server-filtered list --
  // we never reach past the current view.
  const selectAllVisible = () => {
    const visibleCodes = (items || []).map((it) => it.product.jewel_code);
    if (!visibleCodes.length) return;
    setBulkSelected((s) => {
      const allSelected = visibleCodes.every((c) => s.has(c));
      if (allSelected) return new Set();
      return new Set(visibleCodes);
    });
  };

  const doBulkCatalog = async (value) => {
    if (bulkSelected.size === 0) return;
    const codes = Array.from(bulkSelected);
    const action = value ? "Add" : "Remove";
    if (!window.confirm(`${action} ${codes.length} product(s) ${value ? "to" : "from"} the Distribution Catalog?`)) return;
    try {
      const { data } = await api.post("/products/distribution-catalog/bulk", { jewel_codes: codes, value });
      toast.success(`${action}ed: ${data.modified} product(s)`);
      setBulkSelected(new Set());
      setBulkMode(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  const doBulkDelete = async () => {
    if (bulkSelected.size === 0 || bulkDeleting) return;
    const codes = Array.from(bulkSelected);
    setBulkDeleting(true);
    try {
      const { data } = await api.post("/products/bulk-delete", { jewel_codes: codes });
      toast.success(`Deleted ${(data.deleted || []).length} product(s). Recoverable in HO Trash.`);
      setBulkSelected(new Set());
      setBulkMode(false);
      setBulkDeleteOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
    } finally {
      setBulkDeleting(false);
    }
  };

  const toggleSingleCatalog = async (jc, currentValue) => {
    const newValue = !currentValue;
    try {
      await api.patch(`/products/${encodeURIComponent(jc)}/distribution-catalog`, { value: newValue });
      toast.success(newValue ? `Added ${jc} to Distribution Catalog` : `Removed ${jc} from Distribution Catalog`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  const sortedItems = useMemo(() => {
    if (!items || !sort) return items;
    const getVal = (it) => {
      switch (sort.key) {
        case "net_weight": return Number(it.product.net_weight) || 0;
        case "company": return Number(it.pricing?.company?.total) || 0;
        case "franchise": return Number(it.pricing?.franchise?.total) || 0;
        case "customer": return Number(it.pricing?.customer?.total) || 0;
        case "gp": return Number(it.pricing?.gp?.company_gp_pct) || 0;
        default: return 0;
      }
    };
    const arr = [...items];
    arr.sort((a, b) => {
      const av = getVal(a), bv = getVal(b);
      return sort.dir === "asc" ? av - bv : bv - av;
    });
    return arr;
  }, [items, sort]);

  const renderedItems = sortedItems;

  return (
    <div className="space-y-8" data-testid="products-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-[#C9A84C] mb-2">Catalogue</p>
          <h1 className="font-serif text-4xl md:text-5xl text-[#0D1B2A]">Products</h1>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setView("grid")} data-testid="view-grid-btn"
            className={`p-2.5 rounded-md border ${view === "grid" ? "bg-[#0D1B2A] text-white border-[#0D1B2A]" : "border-[#0D1B2A]/15 text-slate-500"}`}>
            <Grid2x2 className="w-4 h-4" strokeWidth={1.5} />
          </button>
          <button onClick={() => setView("list")} data-testid="view-list-btn"
            className={`p-2.5 rounded-md border ${view === "list" ? "bg-[#0D1B2A] text-white border-[#0D1B2A]" : "border-[#0D1B2A]/15 text-slate-500"}`}>
            <List className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>
      </div>

      <div className="luxe-card p-5">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="md:col-span-2 relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" strokeWidth={1.5} />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search Jewel Code or Style…"
              className="pl-10 h-11 bg-white border-[#0D1B2A]/15"
              data-testid="products-search-input"
            />
          </div>
          <Select value={source || "__all__"} onValueChange={(v) => setSource(v === "__all__" ? "" : v)}>
            <SelectTrigger className="h-11 bg-white border-[#0D1B2A]/15" data-testid="filter-source"><SelectValue placeholder="Source" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All sources</SelectItem>
              <SelectItem value="Head Office Stock">Head Office Stock</SelectItem>
              {franchises.length > 0 && (
                <SelectGroup>
                  <SelectLabel className="text-[10px] uppercase tracking-wider text-slate-400">Franchises</SelectLabel>
                  {franchises.map((f) => <SelectItem key={`f-${f.id}`} value={f.name}>{f.name}</SelectItem>)}
                </SelectGroup>
              )}
              {distributors.length > 0 && (
                <SelectGroup>
                  <SelectLabel className="text-[10px] uppercase tracking-wider text-slate-400">Distributors</SelectLabel>
                  {distributors.map((d) => <SelectItem key={`d-${d.id}`} value={d.name}>{d.name}</SelectItem>)}
                </SelectGroup>
              )}
              <SelectItem value="Unassigned">Unassigned</SelectItem>
            </SelectContent>
          </Select>
          <div className="grid grid-cols-3 gap-3">
            <Select value={category || "__all__"} onValueChange={(v) => setCategory(v === "__all__" ? "" : v)}>
              <SelectTrigger className="h-11 bg-white border-[#0D1B2A]/15" data-testid="filter-category"><SelectValue placeholder="Category" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All categories</SelectItem>
                {CATEGORY_OPTIONS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={purity || "__all__"} onValueChange={(v) => setPurity(v === "__all__" ? "" : v)}>
              <SelectTrigger className="h-11 bg-white border-[#0D1B2A]/15" data-testid="filter-purity"><SelectValue placeholder="Purity" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All purities</SelectItem>
                {PURITY_OPTIONS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={memoFilter} onValueChange={setMemoFilter}>
              <SelectTrigger className="h-11 bg-white border-[#0D1B2A]/15" data-testid="filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All status</SelectItem>
                <SelectItem value="memo">Memo only</SelectItem>
                <SelectItem value="sold">Sold only</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-4 flex flex-wrap gap-3 items-center pt-1">
            <Select value={distFilter} onValueChange={setDistFilter}>
              <SelectTrigger className="h-9 w-56 bg-white border-[#0D1B2A]/15 text-xs" data-testid="filter-dist-catalog">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Distribution: All</SelectItem>
                <SelectItem value="in">Distribution: In catalog</SelectItem>
                <SelectItem value="out">Distribution: Not in catalog</SelectItem>
              </SelectContent>
            </Select>
            {canEditDist && (
              <Button
                variant={bulkMode ? "default" : "outline"}
                size="sm"
                onClick={() => { setBulkMode((b) => !b); setBulkSelected(new Set()); }}
                className={bulkMode ? "bg-[#0D1B2A] text-white" : ""}
                data-testid="bulk-mode-toggle"
              >
                <CheckSquare className="w-3.5 h-3.5 mr-1.5" />
                {bulkMode ? `Bulk mode (${bulkSelected.size})` : "Bulk select"}
              </Button>
            )}
            {/* CS 21.54 -- Filter-aware "Select All". Selects only the
                products currently matching the applied filters/search.
                Auto-activates bulk mode so the picks are actionable. */}
            {canEditDist && (items?.length || 0) > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  if (!bulkMode) setBulkMode(true);
                  selectAllVisible();
                }}
                data-testid="bulk-select-all"
                title={`Select all ${items?.length || 0} filtered product(s)`}
              >
                <CheckSquare className="w-3.5 h-3.5 mr-1.5" />
                {(() => {
                  const visible = items?.length || 0;
                  const picked = (items || []).filter((it) => bulkSelected.has(it.product.jewel_code)).length;
                  const allPicked = visible > 0 && picked === visible;
                  return allPicked ? `Clear all (${visible})` : `Select all (${visible})`;
                })()}
              </Button>
            )}
            {bulkMode && bulkSelected.size > 0 && (
              <div className="flex gap-2 ml-auto flex-wrap">
                <Button
                  size="sm"
                  onClick={() => doBulkCatalog(true)}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="bulk-add-catalog"
                >
                  <Sparkles className="w-3.5 h-3.5 mr-1" /> Add {bulkSelected.size} to catalog
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => doBulkCatalog(false)}
                  className="border-red-300 text-red-700 hover:bg-red-50"
                  data-testid="bulk-remove-catalog"
                >
                  Remove {bulkSelected.size} from catalog
                </Button>
                {/* CS 21.42 -- Add to wholesaler */}
                <Button
                  size="sm"
                  onClick={() => setAtwOpen(true)}
                  className="bg-[#0D1B2A] hover:bg-[#0D1B2A]/90 text-white"
                  data-testid="bulk-add-to-wholesaler"
                >
                  <Building2 className="w-3.5 h-3.5 mr-1" /> Add {bulkSelected.size} to wholesaler
                </Button>
                {/* CS 21.86 (APS v8) — Delete selected (HO Products).
                    Same role as single delete (`require_master_admin`);
                    confirmation modal only, NO PIN. Soft-delete via the
                    existing `is_deleted` flag; recoverable from HO Trash. */}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setBulkDeleteOpen(true)}
                  className="border-red-500 text-red-700 hover:bg-red-50"
                  data-testid="bulk-delete-selected"
                >
                  <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete {bulkSelected.size}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {items === null ? (
        <div className={view === "grid" ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" : "space-y-4"}>
          {Array.from({ length: 8 }).map((_, i) => <div key={`skel-${i}`} className="skeleton h-56" />)}
        </div>
      ) : items.length === 0 ? (
        <div className="luxe-card p-16 text-center">
          <Filter className="w-10 h-10 mx-auto text-[#C9A84C] mb-4" strokeWidth={1.5} />
          <h3 className="font-serif text-2xl text-[#0D1B2A] mb-2">No products found</h3>
          <p className="text-slate-500 text-sm">Try different filters or upload a new Excel.</p>
        </div>
      ) : view === "grid" ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {renderedItems.map((it) => (
            <Card
              key={it.product.jewel_code}
              item={it}
              onClick={() => {
                if (bulkMode) toggleBulkPick(it.product.jewel_code);
                else setSelected(it.product.jewel_code);
              }}
              bulkMode={bulkMode}
              bulkChecked={bulkSelected.has(it.product.jewel_code)}
              canEditDist={canEditDist}
              onToggleCatalog={() => toggleSingleCatalog(it.product.jewel_code, it.product.distribution_catalog)}
            />
          ))}
        </div>
      ) : (
        <div className="luxe-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#0D1B2A]/3 text-left">
              <tr className="text-xs uppercase tracking-wider text-slate-500">
                <th className="px-6 py-4">Jewel Code</th>
                <th className="px-6 py-4">Style</th>
                <th className="px-6 py-4">Category</th>
                <th className="px-6 py-4">Purity</th>
                <SortableTh label="Net Wt" sortKey="net_weight" sort={sort} onSort={cycleSort} />
                <SortableTh label="Company Cost" sortKey="company" sort={sort} onSort={cycleSort} />
                <SortableTh label="Franchise" sortKey="franchise" sort={sort} onSort={cycleSort} />
                <SortableTh label="Customer MRP" sortKey="customer" sort={sort} onSort={cycleSort} accent />
                {showGP && <SortableTh label="GP %" sortKey="gp" sort={sort} onSort={cycleSort} hideOnMobile />}
              </tr>
            </thead>
            <tbody>
              {renderedItems.map((it) => (
                <tr key={it.product.jewel_code}
                  onClick={() => setSelected(it.product.jewel_code)}
                  data-testid={`list-row-${it.product.jewel_code}`}
                  className="border-t border-[#0D1B2A]/5 hover:bg-[#FAF8F3] cursor-pointer">
                  <td className="px-6 py-4 font-medium text-[#0D1B2A] whitespace-nowrap">
                    <div className="inline-flex items-center gap-2">
                      <span>{it.product.jewel_code}</span>
                      {it.product.is_memo && <MemoBadge />}
                      <AshthLiteBadge product={it.product} size="xs" />
                    </div>
                  </td>
                  <td className="px-6 py-4 text-slate-600">{it.product.style_no}</td>
                  <td className="px-6 py-4 text-slate-600">{it.product.category}</td>
                  <td className="px-6 py-4"><span className={`text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full border ${purityColor(it.product.purity)}`}>{it.product.purity}</span></td>
                  <td className="px-6 py-4 text-right tabular-nums">{formatWeight(it.product.net_weight)} g</td>
                  <td className="px-6 py-4 text-right tabular-nums text-slate-700">{rupees(it.pricing.company.total)}</td>
                  <td className="px-6 py-4 text-right tabular-nums text-slate-700">{rupees(it.pricing.franchise.total)}</td>
                  <td className="px-6 py-4 text-right tabular-nums font-medium text-[#0D1B2A] border-l-2 border-[#C9A84C]/40 bg-[#C9A84C]/5">{rupees(it.pricing.customer.total)}</td>
                  {showGP && (
                    <td className="px-6 py-4 text-right tabular-nums font-semibold text-[#C9A84C] hidden md:table-cell" data-testid={`gp-cell-${it.product.jewel_code}`}>
                      {it.pricing.gp?.company_gp_pct != null ? `${it.pricing.gp.company_gp_pct.toFixed(1)}%` : "—"}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <ProductDetailDrawer jewelCode={selected} onClose={() => setSelected(null)} level="company"
          onDeleted={() => load()} onEdited={() => load()} />
      )}

      {/* CS 21.42 -- Add designs to a wholesaler */}
      <AddToWholesalerDialog
        open={atwOpen}
        onOpenChange={setAtwOpen}
        jewelCodes={Array.from(bulkSelected)}
        onDone={() => { setBulkSelected(new Set()); setBulkMode(false); }}
      />

      {/* CS 21.86 (v8) — HO Products bulk-delete confirmation.
          No PIN; existing master_admin auth + confirmation only. */}
      {bulkDeleteOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="bulk-delete-dialog">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h2 className="font-serif text-2xl text-[#0D1B2A] mb-2">Delete {bulkSelected.size} product{bulkSelected.size === 1 ? "" : "s"}?</h2>
            <p className="text-sm text-slate-600 mb-3">
              These products will be removed from main inventory and will disappear from every active franchise and distributor link. Deleted pieces are recoverable from HO Trash.
            </p>
            <div className="max-h-32 overflow-auto rounded border border-slate-200 bg-slate-50 p-2 mb-4">
              <ul className="text-xs text-slate-700 space-y-0.5">
                {Array.from(bulkSelected).map((c) => (
                  <li key={c} className="tabular-nums">{c}</li>
                ))}
              </ul>
            </div>
            <div className="flex items-center justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setBulkDeleteOpen(false)}
                disabled={bulkDeleting}
                data-testid="bulk-delete-cancel"
              >Cancel</Button>
              <Button
                className="bg-red-600 hover:bg-red-700 text-white"
                onClick={doBulkDelete}
                disabled={bulkDeleting}
                data-testid="bulk-delete-confirm"
              >
                {bulkDeleting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1" />}
                Delete {bulkSelected.size}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Card({ item, onClick, bulkMode, bulkChecked, canEditDist, onToggleCatalog }) {
  const p = item.product;
  const pr = item.pricing;
  const inCatalog = !!p.distribution_catalog;
  return (
    <div
      onClick={onClick}
      data-testid={`product-card-${p.jewel_code}`}
      className={`luxe-card text-left p-0 overflow-hidden group cursor-pointer transition ${
        bulkMode && bulkChecked ? "ring-2 ring-[#C9A84C] border-[#C9A84C]" : ""
      }`}
    >
      <div className="aspect-square bg-[#FAF8F3] relative overflow-hidden flex items-center justify-center">
        {p.photo_url ? (
          <img src={p.photo_url} alt={p.jewel_code} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        ) : (
          <PhotoPlaceholder />
        )}
        {bulkMode && (
          <div className="absolute top-3 left-3 bg-white/95 rounded p-1 shadow" onClick={(e) => e.stopPropagation()}>
            <Checkbox checked={bulkChecked} onCheckedChange={onClick} data-testid={`bulk-check-${p.jewel_code}`} />
          </div>
        )}
        <span className="absolute top-3 right-3 text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full border bg-white/80 backdrop-blur border-[#0D1B2A]/10 text-slate-700">{p.source_tag}</span>
        {!bulkMode && p.is_memo && <div className="absolute top-3 left-3"><MemoBadge /></div>}
        {inCatalog && (
          <div className="absolute bottom-3 left-3 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-1 rounded-full bg-[#b8893b] text-white shadow" data-testid={`dist-flag-${p.jewel_code}`}>
            <Sparkles className="w-3 h-3" /> Distribution
          </div>
        )}
      </div>
      <div className="p-5 space-y-3">
        <div className="flex justify-between items-start">
          <div className="min-w-0">
            <div className="font-medium text-[#0D1B2A] flex items-center gap-1.5 flex-wrap">
              <span>{p.jewel_code}</span>
              <AshthLiteBadge product={p} size="xs" />
            </div>
            <div className="text-xs text-slate-500 mt-0.5">{p.style_no}</div>
          </div>
          <span className={`text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full border shrink-0 ${purityColor(p.purity)}`}>{p.purity}</span>
        </div>
        <div className="flex justify-between text-xs text-slate-500">
          <span>{p.category}</span>
          <span className="tabular-nums">{formatWeight(p.net_weight)} g</span>
        </div>
        {(pr.total_diamond_pcs > 0 || pr.total_diamond_ct > 0) && (
          <div className="flex justify-between text-[11px] text-slate-500 -mt-1" data-testid={`card-diamonds-${p.jewel_code}`}>
            <span className="uppercase tracking-wider text-[10px] text-slate-400">Diamonds</span>
            <span className="tabular-nums">
              {pr.total_diamond_pcs > 0 && `${formatIndian(pr.total_diamond_pcs)} pcs · `}
              {formatIndian(pr.total_diamond_ct, { decimals: 3 })} ct
            </span>
          </div>
        )}
        <div className="pt-3 border-t border-[#0D1B2A]/5 space-y-1">
          <PriceRow label="Company" value={pr.company.total} />
          <PriceRow label="Franchise" value={pr.franchise.total} />
          <div className="flex justify-between items-baseline pt-1 border-l-2 border-[#C9A84C] pl-2 -ml-2">
            <span className="text-[11px] uppercase tracking-wider text-[#C9A84C]">Customer MRP</span>
            <span className="font-serif text-xl text-[#0D1B2A] tabular-nums">{rupees(pr.customer.total)}</span>
          </div>
        </div>
        {canEditDist && !bulkMode && (
          <button
            onClick={(e) => { e.stopPropagation(); onToggleCatalog(); }}
            className={`w-full mt-1 text-[11px] uppercase tracking-wider py-1.5 rounded border transition ${
              inCatalog
                ? "border-red-200 text-red-700 hover:bg-red-50"
                : "border-[#b8893b] text-[#b8893b] hover:bg-[#b8893b]/5"
            }`}
            data-testid={`toggle-dist-${p.jewel_code}`}
          >
            {inCatalog ? "Remove from Distribution Catalog" : "Add to Distribution Catalog"}
          </button>
        )}
      </div>
    </div>
  );
}

function PriceRow({ label, value }) {
  return (
    <div className="flex justify-between text-xs">
      <span className="text-slate-500">{label}</span>
      <span className="tabular-nums text-slate-700">{rupees(value)}</span>
    </div>
  );
}

function SortableTh({ label, sortKey, sort, onSort, accent, hideOnMobile }) {
  const active = sort?.key === sortKey;
  const dir = active ? sort.dir : null;
  const accentCls = accent ? "border-l-2 border-[#C9A84C]/40 bg-[#C9A84C]/5" : "";
  const hideCls = hideOnMobile ? "hidden md:table-cell" : "";
  return (
    <th
      onClick={() => onSort(sortKey)}
      data-testid={`sort-${sortKey}`}
      className={`px-6 py-4 text-right cursor-pointer select-none hover:bg-[#0D1B2A]/5 transition-colors ${accentCls} ${hideCls} ${active ? "text-[#C9A84C]" : ""}`}
    >
      <span className="inline-flex items-center gap-1 justify-end w-full">
        {label}
        {!active && <ChevronsUpDown className="w-3 h-3 text-slate-400" strokeWidth={2} data-testid={`sort-icon-${sortKey}`} />}
        {active && dir === "asc" && <ChevronUp className="w-3.5 h-3.5 text-[#C9A84C]" strokeWidth={2.5} data-testid={`sort-icon-asc-${sortKey}`} />}
        {active && dir === "desc" && <ChevronDown className="w-3.5 h-3.5 text-[#C9A84C]" strokeWidth={2.5} data-testid={`sort-icon-desc-${sortKey}`} />}
      </span>
    </th>
  );
}
